import React, { useState } from 'react';
import dayjs from 'dayjs';
import { Grid, List, Users, BarChart3 } from 'lucide-react';
import ExpandableTable from '../../components/ExpandableTable';

const DrawsExpandedContent = ({ item, gameObject, allReports, headers }) => {
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'cards'
  const [activeTab, setActiveTab] = useState('draws'); // 'draws' or 'downlines'

  // Get all unique draw times across all games
  const getAllDrawTimes = () => {
    const allDrawTimes = new Set();
    
    // Iterate through all games in the item.draws to get their draw times
    Object.keys(item.draws).forEach(gameId => {
      const game = gameObject[gameId];
      if (game?.otherSettings?.drawTime) {
        game.otherSettings.drawTime.forEach(drawTime => {
          allDrawTimes.add(drawTime);
        });
      }
    });
    
    // Convert to array and sort by time
    return Array.from(allDrawTimes).sort((a, b) => {
      // Convert time strings to comparable format
      const timeA = dayjs(a, 'HH:mm');
      const timeB = dayjs(b, 'HH:mm');
      return timeA.isAfter(timeB) ? 1 : -1;
    });
  };

  const drawTimes = getAllDrawTimes();

  // Calculate totals for each draw time
  const calculateDrawTimeTotals = (drawTime) => {
    let totalSum = 0;
    let totalHits = 0;
    
    Object.entries(item.draws).forEach(([gameId, draws]) => {
      if (draws[drawTime]) {
        totalSum += draws[drawTime].sum || 0;
        totalHits += draws[drawTime].hits || 0;
      }
    });
    
    return { totalSum, totalHits };
  };

  // Calculate grand totals
  const calculateGrandTotals = () => {
    let grandTotalSum = 0;
    let grandTotalHits = 0;
    
    Object.entries(item.draws).forEach(([gameId, draws]) => {
      Object.values(draws).forEach(draw => {
        grandTotalSum += draw.sum || 0;
        grandTotalHits += draw.hits || 0;
      });
    });
    
    return { grandTotalSum, grandTotalHits };
  };

  const { grandTotalSum, grandTotalHits } = calculateGrandTotals();

  // Get downline reports for this user
  const downlineReports = React.useMemo(() => {
    if (!allReports?.userReports || !item.user_id) return [];
    
    const allReportsArray = Object.values(allReports.userReports);
    return allReportsArray.filter(report => 
      report.user_parent.includes(item.user_id) && report.user_id !== item.user_id
    ).sort((a, b) => a.name.trim().localeCompare(b.name.trim()));
  }, [allReports, item.user_id]);

  // Card view component for each draw time
  const DrawListForTime = ({ item, drawTime, gameObject }) => {
    return (
      <div className='space-y-3'>
        {Object.entries(item.draws).map(([key, draw]) => {
          const drawData = draw[drawTime];
          if (!drawData) return null;
          
          return (
            <div className='flex justify-between items-center bg-gray-50 rounded-lg p-4 border' key={key}>
              <div className='flex flex-col'>
                <div className='text-sm font-medium text-gray-900'>{gameObject[key]?.name || `Game ${key}`}</div>
                <div className='text-xs text-gray-500'>Game ID: {key}</div>
              </div>
              <div className='text-right'>
                <div className='text-lg font-bold text-gray-900'>
                  ₱{new Intl.NumberFormat('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }).format(drawData.sum)}
                </div>
                {drawData.hits > 0 && (
                  <div className='text-xs text-red-600 font-medium'>
                    Hits: ₱{new Intl.NumberFormat('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }).format(drawData.hits)}
                  </div>
                )}
                {drawData.hits === 0 && (
                  <div className='text-xs text-gray-400'>No hits</div>
                )}
              </div>
            </div>
          );
        })}
        {Object.keys(item.draws).filter(gameId => item.draws[gameId][drawTime]).length > 1 && (
          <div className='flex justify-between items-center bg-blue-50 rounded-lg p-4 border-2 border-blue-200 mt-3'>
            <div className='text-sm font-bold text-blue-700 uppercase tracking-wide'>Total for this Draw</div>
            <div className='text-lg font-bold text-blue-700'>
              ₱{(() => {
                const { totalSum } = calculateDrawTimeTotals(drawTime);
                return new Intl.NumberFormat('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }).format(totalSum);
              })()}
            </div>
          </div>
        )}
      </div>
    );
  };

  if (drawTimes.length === 0) {
    return (
      <div className="p-6 text-center">
        <div className="text-gray-400 text-4xl mb-4">📊</div>
        <p className="text-lg text-gray-500">No draw data available</p>
        <p className="text-sm text-gray-400 mt-2">No betting records found for this user</p>
      </div>
    );
  }

  const renderTableView = () => (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left font-semibold text-gray-900 sticky left-0 bg-gray-50 z-10 border-r border-gray-200">
                Game
              </th>
              {drawTimes.map((drawTime, index) => (
                <th key={drawTime} className="px-4 py-3 text-center font-semibold text-gray-900 min-w-[120px] border-r border-gray-200 last:border-r-0">
                  <div className="text-xs text-gray-500 uppercase tracking-wide">
                    Draw {index + 1}
                  </div>
                  <div className="text-sm font-bold text-blue-600">
                    {dayjs(drawTime, 'HH:mm').format('h:mm A')}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {Object.entries(item.draws).map(([gameId, draws]) => (
              <tr key={gameId} className="hover:bg-gray-50">
                <td className="px-4 py-3 font-medium text-gray-900 sticky left-0 bg-white z-10 border-r border-gray-200">
                  <div className="flex flex-col">
                    <div className="font-semibold">{gameObject[gameId]?.name || `Game ${gameId}`}</div>
                    <div className="text-xs text-gray-500">ID: {gameId}</div>
                  </div>
                </td>
                {drawTimes.map((drawTime) => {
                  const drawData = draws[drawTime];
                  return (
                    <td key={drawTime} className="px-4 py-3 text-center border-r border-gray-200 last:border-r-0">
                      {drawData ? (
                        <div className="space-y-1">
                          <div className="font-semibold text-gray-900">
                            ₱{new Intl.NumberFormat('en-US', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }).format(drawData.sum)}
                          </div>
                          {drawData.hits > 0 && (
                            <div className="text-xs text-red-600 font-medium">
                              Hits: ₱{new Intl.NumberFormat('en-US', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              }).format(drawData.hits)}
                            </div>
                          )}
                          {drawData.hits === 0 && (
                            <div className="text-xs text-gray-400">No hits</div>
                          )}
                        </div>
                      ) : (
                        <div className="text-gray-400 text-xs">—</div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
            
            {/* Totals Row */}
            <tr className="bg-blue-50 border-t-2 border-blue-200 font-semibold">
              <td className="px-4 py-3 text-blue-900 sticky left-0 bg-blue-50 z-10 border-r border-blue-200">
                <div className="font-bold">TOTALS</div>
              </td>
              {drawTimes.map((drawTime) => {
                const { totalSum, totalHits } = calculateDrawTimeTotals(drawTime);
                return (
                  <td key={drawTime} className="px-4 py-3 text-center border-r border-blue-200 last:border-r-0">
                    <div className="space-y-1">
                      <div className="font-bold text-blue-900">
                        ₱{new Intl.NumberFormat('en-US', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        }).format(totalSum)}
                      </div>
                      {totalHits > 0 && (
                        <div className="text-xs text-red-700 font-medium">
                          Hits: ₱{new Intl.NumberFormat('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          }).format(totalHits)}
                        </div>
                      )}
                    </div>
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderCardView = () => (
    <div className={`grid gap-6 ${
      drawTimes.length === 1 ? 'grid-cols-1' :
      drawTimes.length === 2 ? 'grid-cols-1 sm:grid-cols-2' :
      'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'
    }`}>
      {drawTimes.map((drawTime, index) => (
        <div key={drawTime} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          {/* Draw time header */}
          <div className="bg-gray-900 text-white p-4 text-center">
            <div className="text-xs font-semibold uppercase tracking-wider opacity-75">
              Draw {index + 1}
            </div>
            <div className="text-xl font-bold">
              {dayjs(drawTime, 'HH:mm').format('h:mm A')}
            </div>
          </div>
          
          {/* Draw content */}
          <div className="p-4">
            <DrawListForTime
              item={item}
              drawTime={drawTime}
              gameObject={gameObject}
            />
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header with user info */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200">
        <div className="flex justify-between items-start mb-2">
          <div>
            <h3 className="text-lg font-bold text-gray-900 mb-1">{item.name}</h3>
            <p className="text-sm text-gray-600">
              {Object.keys(item.draws).length} {Object.keys(item.draws).length === 1 ? 'Game' : 'Games'} • 
              {drawTimes.length} {drawTimes.length === 1 ? 'Draw Time' : 'Draw Times'} •
              {downlineReports.length} {downlineReports.length === 1 ? 'Downline' : 'Downlines'}
            </p>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
        <button
          onClick={() => setActiveTab('draws')}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
            activeTab === 'draws'
              ? 'bg-white text-blue-600 shadow-sm'
              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
          }`}
        >
          <BarChart3 className="w-4 h-4" />
          Draw Details
        </button>
        <button
          onClick={() => setActiveTab('downlines')}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
            activeTab === 'downlines'
              ? 'bg-white text-blue-600 shadow-sm'
              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
          }`}
        >
          <Users className="w-4 h-4" />
          Downlines ({downlineReports.length})
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'draws' ? (
        <div className="space-y-6">
          {drawTimes.length === 0 ? (
            <div className="p-6 text-center">
              <div className="text-gray-400 text-4xl mb-4">📊</div>
              <p className="text-lg text-gray-500">No draw data available</p>
              <p className="text-sm text-gray-400 mt-2">No betting records found for this user</p>
            </div>
          ) : (
            <>
              {/* View Toggle for Draw Details */}
              <div className="flex justify-end">
                <div className="flex bg-white rounded-lg p-1 border border-gray-300 shadow-sm">
                  <button
                    onClick={() => setViewMode('table')}
                    className={`flex items-center gap-2 px-3 py-2 rounded-md text-xs font-medium transition-colors ${
                      viewMode === 'table'
                        ? 'bg-blue-600 text-white shadow-sm'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    <List className="w-4 h-4" />
                    Table
                  </button>
                  <button
                    onClick={() => setViewMode('cards')}
                    className={`flex items-center gap-2 px-3 py-2 rounded-md text-xs font-medium transition-colors ${
                      viewMode === 'cards'
                        ? 'bg-blue-600 text-white shadow-sm'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    <Grid className="w-4 h-4" />
                    Cards
                  </button>
                </div>
              </div>

              {/* Content based on view mode */}
              {viewMode === 'table' ? renderTableView() : renderCardView()}

              {/* Summary section */}
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 border border-green-200">
                <h4 className="text-lg font-bold text-gray-900 mb-3">Summary</h4>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-bold text-green-600">
                      ₱{new Intl.NumberFormat('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      }).format(grandTotalSum)}
                    </div>
                    <div className="text-xs text-gray-600 font-medium">Total Sales</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-red-600">
                      ₱{new Intl.NumberFormat('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      }).format(grandTotalHits)}
                    </div>
                    <div className="text-xs text-gray-600 font-medium">Total Hits</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-blue-600">{Object.keys(item.draws).length}</div>
                    <div className="text-xs text-gray-600 font-medium">Games</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-purple-600">{drawTimes.length}</div>
                    <div className="text-xs text-gray-600 font-medium">Draw Times</div>
                  </div>
                </div>
                
                {/* Net calculation */}
                <div className="mt-4 pt-4 border-t border-green-200">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-gray-900">
                      ₱{new Intl.NumberFormat('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      }).format(grandTotalSum - grandTotalHits)}
                    </div>
                    <div className="text-sm text-gray-600 font-medium">Net Sales (Sales - Hits)</div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <h4 className="text-lg font-semibold text-gray-900">Downline Reports</h4>
          {downlineReports.length === 0 ? (
            <div className="p-8 text-center bg-gray-50 rounded-lg">
              <div className="text-gray-400 text-4xl mb-4">👥</div>
              <p className="text-lg text-gray-500">No downlines found</p>
              <p className="text-sm text-gray-400 mt-2">This user has no downline users in the current report period</p>
            </div>
          ) : (
            <ExpandableTable 
              headers={headers || []} 
              items={downlineReports} 
              loading={false}
              expandableContent={({ item: downlineItem }) => (
                <DrawsExpandedContent 
                  item={downlineItem} 
                  gameObject={gameObject}
                  allReports={allReports}
                  headers={headers}
                />
              )}
              expandableKey="user_id"
            />
          )}
        </div>
      )}
    </div>
  );
};

export default DrawsExpandedContent;